import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import authService, { User } from '../services/integratedAuthService';

interface Log {
    id: string;
    timestamp: string;
    system: string;
    error_description?: string;
}

const useStyles = makeStyles({
    root: {
        backgroundColor: '#edebeb', // Updated background color
        minHeight: '100vh',
        padding: '20px',
    },
    tableRow: {
        backgroundColor: '#ffebee', // Light red background
        marginBottom: '10px', // Space between rows
        borderBottom: '2px solid black', // Black line between rows
    },
    headerBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    container: {
        marginTop: '20px',
    },
    card: {
        marginBottom: '20px',
    },
    tableContainer: {
        marginTop: '20px',
    },
});

const Dashboard: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [logs, setLogs] = useState<Log[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [currentTime, setCurrentTime] = useState<string>(new Date().toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).replace(',', ''));

    useEffect(() => {
        // Get user information
        const currentUser = authService.getUser();
        setUser(currentUser);
    }, []);

    const handleLogout = () => {
        authService.logout();
        navigate('/');
    };

    const fetchLogs = async () => {
        try {
            console.log('Fetching logs...');
            const apiUrl = process.env.REACT_APP_ERROR_API_URL || 'https://error-api.monitor.prishops.nl/api/errors';
            console.log('API URL:', apiUrl);
            const response = await axios.get(apiUrl);
            console.log('Response status:', response.status);
            console.log('Response data:', response.data); // Log the result of the GET request
            if (response.data && Array.isArray(response.data)) {
                const logsData: Log[] = response.data.map((record: any) => ({
                    id: record._id,
                    timestamp: record.timestamp,
                    system: record.system,
                    error_description: record.error_description,
                }));
                // Sort logsData by timestamp in descending order (newest first)
                logsData.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                setLogs(logsData);
            } else {
                console.error('Unexpected response data format:', response.data);
            }
        } catch (err) {
            console.error('Failed to fetch logs', err);
        }
    };

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval); // Cleanup interval on component unmount
    }, []);

    useEffect(() => {
        const timeInterval = setInterval(() => {
            setCurrentTime(new Date().toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
            }).replace(',', ''));
        }, 1000);
        return () => clearInterval(timeInterval); // Cleanup interval on component unmount
    }, []);

    const formatDate = (timestamp: string) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    return (
        <Container className={classes.root}>
            <Box className={classes.headerBox}>
                <Typography variant="h4" component="h1" gutterBottom>
                    New Enerday System Overview
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    {user && (
                        <Typography variant="body1" component="div">
                            Welcome, {user.username}
                        </Typography>
                    )}
                    <Typography variant="h4" component="div">
                        {currentTime}
                    </Typography>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={handleLogout}
                        size="small"
                    >
                        Logout
                    </Button>
                </Box>
            </Box>
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><Typography variant="h6" component="div"><strong>Systeem</strong></Typography></TableCell>
                            <TableCell><Typography variant="h6" component="div"><strong>Error omschrijving</strong></Typography></TableCell>
                            <TableCell><Typography variant="h6" component="div"><strong>Tijdstip</strong></Typography></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map(log => (
                            <TableRow key={log.id} className={classes.tableRow}>
                                <TableCell><strong>{log.system}</strong></TableCell>
                                <TableCell>{log.error_description || 'N/A'}</TableCell>
                                <TableCell>{formatDate(log.timestamp)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default Dashboard;